<template>
  <div
    class="selection-timer flex flex-col items-end gap-1 w-full max-w-[400px] md:max-w-[500px] lg:max-w-[600px] transition-opacity duration-300 ease-out"
    :class="{ 'opacity-0 pointer-events-none': !isActive, 'selection-timer--inactive': !isActive }"
    role="progressbar"
    :aria-valuenow="displaySeconds"
    aria-valuemin="0"
    :aria-valuemax="Math.round(totalTime / 1000)"
    :aria-label="`Selection timer: ${formattedTime} remaining`"
  >
    <div
      class="selection-timer__text font-mono text-sm text-arena-text-dim leading-none tabular-nums"
    >
      {{ formattedTime }}
    </div>
    <div class="selection-timer__track w-full h-0.5 bg-arena-border rounded-sm overflow-hidden">
      <div class="selection-timer__fill" :style="{ transform: `scaleX(${progressFraction})` }" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  timeRemaining: number; // milliseconds
  totalTime?: number; // milliseconds (default 30000)
  isActive: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  totalTime: 30000,
});

const progressFraction = computed(() => {
  if (props.totalTime <= 0) return 0;
  return Math.max(0, Math.min(1, props.timeRemaining / props.totalTime));
});

const displaySeconds = computed(() => {
  return Math.max(0, Math.ceil(props.timeRemaining / 1000));
});

const formattedTime = computed(() => {
  const seconds = displaySeconds.value;
  return `0:${String(seconds).padStart(2, '0')}`;
});
</script>

<style scoped>
.selection-timer__fill {
  width: 100%;
  height: 100%;
  background: var(--color-energy-neutral, var(--color-arena-text-dim));
  transform-origin: left;
  transition: transform 100ms linear;
}

@media (prefers-reduced-motion: reduce) {
  .selection-timer__fill {
    transition: none;
  }
}
</style>
